/**
 * Collection of custom input fields based on primereact fields wrapped with a container.
 * Placeholders must come after the input because of how the CSS `~` selector works.
 * NOTE: These inputs should be used at all times.
 *  If a necessary input does not exist in this file, it should be created.
 */

import { forwardRef } from 'react'
import { InputNumber, InputNumberProps } from 'primereact/inputnumber'
import { InputText, InputTextProps } from 'primereact/inputtext'
import { Dropdown, DropdownProps } from 'primereact/dropdown'
import { Calendar, CalendarProps, CalendarSelectionMode } from 'primereact/calendar'

import { Column } from './FlexContainer'
import { DECIMAL_PLACES } from '../../../tools/format'
import { InputTextarea, InputTextareaProps } from 'primereact/inputtextarea'

interface NumberFieldProps extends InputNumberProps {
    unit?: string
    allowDecimals?: boolean
}
export const NumberField = ({ placeholder, unit, allowDecimals, ...props }: NumberFieldProps) => {
    return (
        <Column tag="label" className="input-field" wide>
            <InputNumber
                maxFractionDigits={allowDecimals ? DECIMAL_PLACES : 0}
                mode={allowDecimals ? 'decimal' : undefined}
                format={false}
                {...props}
            />
            <div className="input-field__placeholder">{placeholder}</div>
            {unit && <div className="input-field__unit">{unit}</div>}
        </Column>
    )
}

interface TextFieldProps extends InputTextProps {
    unit?: string
}
export const TextField = ({ placeholder, unit, ...props }: TextFieldProps) => {
    return (
        <Column tag="label" wide className="input-field">
            <InputText {...props} />
            <div className="input-field__placeholder">{placeholder}</div>
            {unit && <div className="input-field__unit">{unit}</div>}
        </Column>
    )
}

interface TextareaFieldProps extends InputTextareaProps {
    approxRows?: number
}
export const TextareaField = ({ placeholder, approxRows, ...props }: TextareaFieldProps) => {
    const rowApproximationAdjustment = 3
    const rowsRem = approxRows ? rowApproximationAdjustment + approxRows + 'rem' : undefined
    return (
        <Column tag="label" wide className="input-field input-field--textarea">
            <InputTextarea {...props} style={{ height: rowsRem }} />
            <div className="input-field__placeholder">{placeholder}</div>
        </Column>
    )
}

interface DropdownFieldProps extends DropdownProps {
    unit?: string
}
export const DropdownField = ({ placeholder, unit, ...props }: DropdownFieldProps) => {
    return (
        <Column tag="label" wide className="input-field">
            <Dropdown {...props} />
            <div className="input-field__placeholder">{placeholder}</div>
            {unit && <div className="input-field__unit">{unit}</div>}
        </Column>
    )
}

interface CalendarFieldProps extends CalendarProps<CalendarSelectionMode> {}
export const CalendarField = forwardRef<Calendar, CalendarFieldProps>(
    ({ placeholder, ...props }: CalendarFieldProps, ref) => (
        <Column tag="label" wide className="input-field input-field--calendar">
            <Calendar ref={ref} {...props} />
            <div className="input-field__placeholder">{placeholder}</div>
        </Column>
    )
)
